import { useEffect,useRef } from 'react';
import { X } from 'lucide-react';
import guides from '../../lib/calculatorGuides.json';
import routes from '../../lib/siteRoutes.json';

interface InfoModalProps { calculatorId: string; isOpen: boolean; onClose: () => void; onDismiss?: () => void; isMobile?: boolean; }

export default function InfoModal({ calculatorId, isOpen, onClose, onDismiss, isMobile = false }: InfoModalProps) {
  const panel = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    const outside = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node) || panel.current?.contains(target)) return;
      // Let the Info button toggle the popup itself, without closing then reopening.
      if (target instanceof Element && target.closest('[aria-controls="calculator-information"]')) return;
      (onDismiss ?? onClose)();
    };
    window.addEventListener('keydown', escape);
    document.addEventListener('pointerdown', outside, true);
    return () => {
      window.removeEventListener('keydown', escape);
      document.removeEventListener('pointerdown', outside, true);
    };
  }, [isOpen, onClose, onDismiss]);
  if (!isOpen) return null;
  const path = `/${calculatorId.replace(/^\//, '')}`;
  const page = routes.find(route => route.path === path);
  const guide = guides[path as keyof typeof guides];
  return (
    <section ref={panel} id="calculator-information" role="dialog" aria-modal="false" aria-label="Calculator information" className={`fixed z-[60] rounded-2xl bg-gray-900 p-5 text-white shadow-xl w-[380px] max-w-[calc(100vw-32px)] max-h-[65vh] overflow-y-auto ${isMobile ? 'top-24 right-4' : 'top-1/4 right-24'}`}>
      <div className="flex justify-between gap-3 mb-4">
        <h2 className="font-semibold">{page?.heading ?? 'Calculator information'}</h2>
        <button aria-label="Close calculator information" onClick={onClose}><X size={20} /></button>
      </div>
      <p>{guide?.intro ?? page?.description ?? 'Review the inputs and limitations shown on the calculator page.'}</p>
      {guide?.sections.map(section => <div key={section.heading} className="mt-4"><h3 className="font-semibold">{section.heading}</h3>{section.paragraphs.map(text => <p key={text} className="mt-2 text-sm text-gray-300">{text}</p>)}</div>)}
      {guide && <div className="mt-4"><h3 className="font-semibold">{guide.example.heading}</h3><p className="mt-2 text-sm text-gray-300">{guide.example.text}</p></div>}
      <p className="mt-4 text-sm text-gray-400">Results depend on the inputs and assumptions. Check important decisions independently.</p>
    </section>
  );
}
